import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VideoCard from "@/components/VideoCard";
import { ArrowLeft, UserPlus } from "lucide-react";
import { toast } from "sonner";

// Windowing constants — MUST match Feed's tight window on mobile.
// Chrome Android caps concurrent MediaSource/decoder instances at ~6 per tab;
// with NEAR_WINDOW=3 we'd have 7 <video> elements each holding a MediaSource
// and hit the cap on the 3rd swipe, manifesting as "Stream Unavailable".
// NEAR_WINDOW=1 = only prev/current/next attach a <video> (3 concurrent decoders),
// well under the cap even after slow cleanup on rapid swipes.
// RENDER_WINDOW=4 still mounts 4 posters ahead so scrolling stays instant.
const RENDER_WINDOW = 4;
const NEAR_WINDOW = 1;

export default function VideoView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [videos, setVideos] = useState(null); // null = loading, [] = not found
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  // Creator profile shape (only fetched in profile-scroll mode) — used to
  // gate the "+ FOLLOW" pill on whether the viewer is already following.
  const [creator, setCreator] = useState(null);
  const containerRef = useRef(null);
  const tipPollHandled = useRef(false);

  // Context-aware sibling loading. If the URL carries `?u=<username>`
  // (arrived from a profile grid), we scroll through that creator's
  // library. If it carries `?src=discover` (arrived from Discover/Trends
  // grid), we pull the same algorithmic feed so the viewer can keep
  // swiping through recommendations — same UX as the main For-You feed.
  // Otherwise fall back to just the single video (deep-link, share URL).
  const profileUsername = searchParams.get("u");
  const feedSource = searchParams.get("src");
  const referrer = searchParams.get("ref");
  // Referral ribbon self-dismisses after 3s so it doesn't hog the top of
  // the screen while the viewer is watching.
  const [refRibbonGone, setRefRibbonGone] = useState(false);
  useEffect(() => {
    if (!referrer) return;
    setRefRibbonGone(false);
    const t = setTimeout(() => setRefRibbonGone(true), 3000);
    return () => clearTimeout(t);
  }, [referrer]);

  // Fire a "share visit" ping when someone lands via a shared link with a
  // ?ref= attribution. Dedupe per (video, viewer, day) on the server so
  // repeat visits from the same viewer only count once. Fire-and-forget.
  // ALSO: persist the ref in localStorage (30-day TTL) so if this viewer
  // signs up later, the /auth/session call can forward it and permanently
  // attribute them to this sharer for 10% boost commissions.
  useEffect(() => {
    if (!id || !referrer) return;
    try {
      const url = `${window.location.origin.replace(/\/$/, "")}`;
      const target = `${process.env.REACT_APP_BACKEND_URL || url}/api/videos/${id}/share-visit?ref=${encodeURIComponent(referrer)}`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(target);
      } else {
        fetch(target, { method: "POST", keepalive: true }).catch(() => {});
      }
    } catch { /* never block the video from loading */ }
    // Save ref for the eventual signup — 30 day window is generous enough
    // that a WhatsApp share on a Monday still credits the sharer on Friday.
    try {
      const clean = String(referrer).replace(/^@/, "").trim().toLowerCase();
      if (clean) {
        localStorage.setItem("neosync_signup_ref", JSON.stringify({
          ref: clean,
          via_video_id: id,
          saved_at: Date.now(),
        }));
      }
    } catch { /* ignore quota / private-mode errors */ }
  }, [id, referrer]);

  const load = useCallback(async () => {
    try {
      if (profileUsername) {
        const [videosRes, profileRes] = await Promise.all([
          api.get(`/users/${encodeURIComponent(profileUsername)}/videos`),
          api.get(`/users/${encodeURIComponent(profileUsername)}`).catch(() => ({ data: null })),
        ]);
        const list = Array.isArray(videosRes.data) ? videosRes.data : [];
        setCreator(profileRes.data || null);
        // Move the clicked video to the front so it's the initial active card.
        const idx = list.findIndex((v) => v.id === id);
        if (idx > 0) {
          const clicked = list.splice(idx, 1)[0];
          list.unshift(clicked);
        } else if (idx === -1) {
          try {
            const single = await api.get(`/videos/${id}`);
            if (single.data) list.unshift(single.data);
          } catch { /* ignore */ }
        }
        setVideos(list);
      } else if (feedSource === "discover" || feedSource === "trends") {
        // Pull the same feed Discover/Trends grid rendered. Put the
        // tapped video first so it stays the initial active card even
        // if the algorithm reshuffles the order between the grid and
        // this fetch.
        const [feedRes, singleRes] = await Promise.all([
          api.get("/videos/feed", { params: { limit: 24 } }),
          api.get(`/videos/${id}`).catch(() => ({ data: null })),
        ]);
        const list = Array.isArray(feedRes.data) ? feedRes.data.slice() : [];
        const idx = list.findIndex((v) => v.id === id);
        if (idx > 0) {
          const clicked = list.splice(idx, 1)[0];
          list.unshift(clicked);
        } else if (idx === -1 && singleRes.data) {
          list.unshift(singleRes.data);
        }
        setVideos(list);
      } else {
        const r = await api.get(`/videos/${id}`);
        setVideos(r.data ? [r.data] : []);
      }
    } catch {
      setVideos([]);
    }
  }, [id, profileUsername, feedSource]);

  // Follow toggle — optimistic. Fires on tap of the "+ FOLLOW" pill.
  const handleFollow = useCallback(async () => {
    if (!user || !creator || !profileUsername) return;
    setCreator((c) => c ? { ...c, following: true, follower_count: (c.follower_count || 0) + 1 } : c);
    try {
      await api.post(`/users/${encodeURIComponent(profileUsername)}/follow`);
      toast.success(`Following @${profileUsername}`);
    } catch {
      // Roll back
      setCreator((c) => c ? { ...c, following: false, follower_count: Math.max(0, (c.follower_count || 1) - 1) } : c);
      toast.error("Could not follow — try again");
    }
  }, [user, creator, profileUsername]);

  useEffect(() => { load(); }, [load]);

  // Track active card via scroll position.
  //
  // CRITICAL: deps include `videos.length` because on the very first render
  // (while `videos` is still an empty array pending fetch) the scroll
  // container doesn't exist yet, so `containerRef.current` is null and this
  // effect bails out. Without `videos.length` in the deps, it would never
  // re-run once the container actually mounted, and `activeIdx` would be
  // stuck at 0 forever — meaning every card the user scrolls past would
  // stay a black placeholder (distance > RENDER_WINDOW). Feed.jsx has the
  // same guardrail with `loading` in its deps.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const h = el.clientHeight;
      if (!h) return; // container not laid out yet
      const idx = Math.round(el.scrollTop / h);
      if (idx !== activeIdx) setActiveIdx(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeIdx, videos?.length]);

  // Handle return from Stripe Checkout — poll tip status and toast
  useEffect(() => {
    const sessionId = searchParams.get("tip_session_id");
    const cancelled = searchParams.get("tip_cancelled");
    if (cancelled) {
      toast("Boost cancelled", { description: "No charge was made." });
      searchParams.delete("tip_cancelled");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sessionId || tipPollHandled.current) return;
    tipPollHandled.current = true;
    const toastId = toast.loading("Confirming your boost…");
    let attempts = 0;
    const maxAttempts = 8;
    const activeVideo = videos?.[activeIdx];
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/tips/status/${sessionId}`);
        const { payment_status, status, credited, amount_cents } = res.data || {};
        if (credited) {
          toast.success(`Boost sent — $${(amount_cents / 100).toFixed(2)} 🔥`, { id: toastId });
          // Fire the sender-side gift takeover via the global CustomEvent
          // that GiftShowcase listens to. Creator sees it live via
          // WebSocket; this is the sender-side twin.
          if (amount_cents >= 100) {
            window.dispatchEvent(new CustomEvent("neosync:gift-preview", {
              detail: {
                id: sessionId,
                amount_cents,
                mode: "sent",
                tipper_name: user?.name || activeVideo?.author?.name,
                tipper_picture: user?.picture || activeVideo?.author?.picture,
                message: null,
              },
            }));
          }
          searchParams.delete("tip_session_id");
          setSearchParams(searchParams, { replace: true });
          load();
          return;
        }
        if (status === "expired" || payment_status === "failed") {
          toast.error("Boost failed", { id: toastId });
          searchParams.delete("tip_session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          toast("Still processing — refresh in a moment", { id: toastId });
          searchParams.delete("tip_session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.error("Could not verify boost", { id: toastId });
        }
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("tip_session_id"), searchParams.get("tip_cancelled")]);

  if (videos === null) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="h-8 w-8 border-2 border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-black px-6 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-3">
          // dead.link
        </p>
        <h2 className="font-display font-black text-3xl mb-6">Video not found</h2>
        <button
          onClick={() => navigate("/")}
          className="bg-[#39FF14] text-black px-5 py-3 font-bold uppercase tracking-widest"
          data-testid="video-view-back-btn"
        >
          Back to feed
        </button>
      </div>
    );
  }

  // Show the "+ FOLLOW" pill once the viewer is 2+ videos deep in the
  // creator's profile — a high-intent moment. Hide it for logged-out
  // users, the creator themselves, and if the viewer is already following.
  const showFollowPill = Boolean(
    profileUsername &&
    user &&
    creator &&
    !creator.following &&
    creator.username !== user.username &&
    activeIdx >= 1
  );

  return (
    <div className="relative bg-black">
      <button
        onClick={() => navigate(-1)}
        data-testid="video-back-btn"
        className="fixed top-4 left-4 z-30 h-10 w-10 bg-black/50 backdrop-blur-md border border-white/10 hover:border-[#39FF14]/50 flex items-center justify-center"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      {/* High-intent follow prompt — slides in when the user has scrolled
          past the first video in a profile-scroll session. */}
      {showFollowPill && (
        <button
          onClick={handleFollow}
          data-testid="video-view-follow-pill"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 shadow-[0_0_24px_rgba(57,255,20,0.55)] animate-[fadeInDown_0.4s_ease-out]"
          style={{ animation: "vvFollowIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)" }}
        >
          <UserPlus className="h-3.5 w-3.5" strokeWidth={2.5} />
          follow @{creator.username}
        </button>
      )}
      <style>{`
        @keyframes vvFollowIn {
          from { transform: translate(-50%, -20px); opacity: 0; }
          to   { transform: translate(-50%, 0);     opacity: 1; }
        }
        @keyframes vvRefIn {
          from { transform: translate(-50%, 20px); opacity: 0; }
          to   { transform: translate(-50%, 0);    opacity: 1; }
        }
        @keyframes vvRefOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
      `}</style>
      {/* Referral ribbon — appears when the viewer landed via a shared link.
          Signals social-proof and gives the sharer visible credit. */}
      {referrer && !showFollowPill && !refRibbonGone && (
        <div
          data-testid="video-view-referral-ribbon"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/70 backdrop-blur-md border border-[#39FF14]/50 text-white text-[11px] font-mono uppercase tracking-widest px-3 py-1.5"
          style={{ animation: "vvRefIn 0.5s cubic-bezier(0.22, 1, 0.36, 1), vvRefOut 0.3s ease 2.7s forwards" }}
        >
          <span aria-hidden>↗</span>
          <span>shared by</span>
          <button
            type="button"
            onClick={() => navigate(`/u/${referrer}`)}
            className="text-[#39FF14] hover:underline"
          >
            @{referrer}
          </button>
        </div>
      )}
      <div
        ref={containerRef}
        data-testid="video-view-scroller"
        className="h-screen w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide bg-black"
      >
        {videos.map((v, idx) => {
          const distance = Math.abs(idx - activeIdx);
          // Outside render window → lightweight poster placeholder so
          // mobile isn't asked to prep the whole profile at once.
          if (distance > RENDER_WINDOW) {
            return (
              <div
                key={v.id}
                data-testid={`video-view-placeholder-${v.id}`}
                className="h-screen w-full snap-start bg-black"
              />
            );
          }
          return (
            <VideoCard
              key={v.id}
              video={v}
              active={idx === activeIdx}
              nearActive={distance <= NEAR_WINDOW}
              onUpdate={load}
              muted={muted}
              setMuted={setMuted}
            />
          );
        })}
      </div>
    </div>
  );
}
